import { z } from "zod";

// Reserved words that cannot be used as organization slugs
export const RESERVED_SLUGS = [
  // System routes
  "admin",
  "api",
  "www",
  "app",
  "help",
  "support",
  "billing",
  "settings",
  "account",
  "login",
  "logout",
  "register",
  "signup",
  "auth",
  "oauth",
  "dashboard",
  "home",
  "index",
  "default",
  "root",
  "main",
  "public",
  "private",
  "static",
  "assets",
  "cdn",
  "media",
  "files",
  "uploads",
  "downloads",
  "mail",
  "email",
  "ftp",
  "ssh",
  "blog",
  "news",
  "about",
  "contact",
  "terms",
  "privacy",
  "legal",
  "docs",
  "documentation",
  "faq",
  "status",
  "health",
  "ping",
  "test",
  "debug",
  "dev",
  "development",
  "staging",
  "prod",
  "production",
  "beta",
  "alpha",
  "demo",
  "trial",
  "free",
  "premium",
  "pro",
  "enterprise",
  "team",
  "personal",
  "user",
  "users",
  "org",
  "orgs",
  "organization",
  "organizations",
  "company",
  "companies",
  "group",
  "groups",
  "workspace",
  "workspaces",
  "project",
  "projects",
  "client",
  "clients",
  "customer",
  "customers",
  "member",
  "members",
  "staff",
  "employee",
  "employees",
  "admin",
  "administrator",
  "moderator",
  "moderators",
  "owner",
  "managers",
  "manager",
  "guest",
  "guests",
  "null",
  "undefined",
  "true",
  "false",
  "0",
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "10",
];

// Validation schema for organization slug
export const organizationSlugSchema = z.string()
  .min(3, "Slug must be at least 3 characters long")
  .max(50, "Slug must be no more than 50 characters long")
  .regex(/^[a-z0-9-]+$/, "Slug can only contain lowercase letters, numbers, and hyphens")
  .regex(/^[a-z0-9]/, "Slug must start with a letter or number")
  .regex(/[a-z0-9]$/, "Slug must end with a letter or number")
  .refine(
    (slug) => !RESERVED_SLUGS.includes(slug),
    "This slug is reserved and cannot be used"
  )
  .refine(
    (slug) => !slug.includes("--"),
    "Slug cannot contain consecutive hyphens"
  );

// Validation schema for organization creation
export const createOrganizationSchema = z.object({
  name: z.string()
    .min(1, "Organization name is required")
    .max(100, "Organization name must be less than 100 characters")
    .trim(),
  slug: organizationSlugSchema.optional(),
  description: z.string()
    .max(500, "Description must be less than 500 characters")
    .optional()
    .transform(val => val?.trim() || undefined),
  logo_url: z.string().url().optional().or(z.literal("")),
  settings: z.record(z.unknown()).optional().default({}),
  subscription_tier: z.enum(["free", "pro", "enterprise", "personal", "team", "self_hosted"]).default("free"),
});

// Validation schema for organization updates
export const updateOrganizationSchema = createOrganizationSchema
  .partial()
  .omit({ slug: true }); // Slug cannot be updated

// Type definitions
export type CreateOrganizationInput = z.infer<typeof createOrganizationSchema>;
export type UpdateOrganizationInput = z.infer<typeof updateOrganizationSchema>;

// Helper function to validate and generate slug
export function generateValidSlug(name: string): string {
  return name
    .toLowerCase()
    .trim()
    // Remove special characters except spaces and hyphens
    .replace(/[^\w\s-]/g, "")
    // Replace spaces with hyphens
    .replace(/\s+/g, "-")
    // Replace multiple hyphens with single
    .replace(/-+/g, "-")
    // Remove leading/trailing hyphens
    .replace(/^-+|-+$/g, "")
    // Ensure it starts and ends with alphanumeric
    .replace(/^-(.+)-$/, "$1")
    // Truncate if too long
    .substring(0, 50);
}

// Check if a slug is valid
export function isValidSlug(slug: string): boolean {
  try {
    organizationSlugSchema.parse(slug);
    return true;
  } catch {
    return false;
  }
}

// Check if a slug is reserved
export function isReservedSlug(slug: string): boolean {
  return RESERVED_SLUGS.includes(slug.toLowerCase());
}

// Generate unique slug with suffix if needed
export function generateUniqueSlug(baseSlug: string, existingSlugs: string[]): string {
  let slug = baseSlug;
  let counter = 1;
  
  while (existingSlugs.includes(slug)) {
    const suffix = `-${counter}`;
    // Ensure slug doesn't exceed max length
    if (baseSlug.length + suffix.length > 50) {
      slug = baseSlug.substring(0, 50 - suffix.length) + suffix;
    } else {
      slug = baseSlug + suffix;
    }
    counter++;
  }
  
  return slug;
}
